import { useEffect, useState } from "react";
import type { ApiError, CatalogResponse } from "@/types";
import * as api from "@/lib/api";

interface UseCatalogResult {
  data: CatalogResponse | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useCatalog(): UseCatalogResult {
  const [data, setData] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .getCatalog(controller.signal)
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof Error && err.name === "AbortError") return;
        setError(api.isApiError(err) ? err : { error: err instanceof Error ? err.message : "Unknown error" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [attempt]);

  return { data, loading, error, refetch: () => setAttempt((a) => a + 1) };
}
